// ─────────────────────────────────────────────────────────────────────────
// Shared monthly/annual preference for every pricing surface.
//
// Before this, each surface held its own `useState`: /pricing opened on annual,
// the in-app plan screens opened on monthly, and a user who picked Annual on
// /pricing was shown Monthly again the moment they opened the upgrade modal.
// The ticket requires one default (annual) and that the choice PERSISTS across
// the two surfaces, so the state has to live outside any one component tree —
// the marketing page and the modal never share a React root.
//
// Deliberately NOT a React context: the surfaces are mounted by different roots
// (app/pricing, the project page's modal host), so a provider would have to wrap
// both and still wouldn't survive the full page load between them.
//
// This is a display preference only. It never decides what is charged — every
// route re-derives the price from the cadence in the request body server-side
// (see subscribe-rated / subscribe-tiered), so a stale or tampered value here
// cannot mis-bill anyone.
// ─────────────────────────────────────────────────────────────────────────

import { useSyncExternalStore } from "react";

import type { BillingCadence } from "@/lib/pricing/credit-tiers";

export type { BillingCadence };

/** Product rule: every pricing surface opens on annual so the saving is the
 *  first thing a visitor sees. Also the value used for server rendering and
 *  whenever storage is unreadable. */
export const DEFAULT_CADENCE: BillingCadence = "annual";

const STORAGE_KEY = "panda_billing_cadence";

function isCadence(v: unknown): v is BillingCadence {
  return v === "monthly" || v === "annual";
}

// Cached so getSnapshot() returns a stable value between renders instead of
// hitting localStorage on every read. `null` = not yet read this page load.
let cached: BillingCadence | null = null;

const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

/** Current preference. Falls back to the default on SSR, when storage is
 *  blocked (Safari private mode, cookies disabled), or when the stored value is
 *  anything other than the two we accept — a garbage value must not leave the
 *  toggle in a state no button matches. */
export function readCadencePreference(): BillingCadence {
  if (cached !== null) return cached;
  if (typeof window === "undefined") return DEFAULT_CADENCE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    cached = isCadence(raw) ? raw : DEFAULT_CADENCE;
  } catch {
    cached = DEFAULT_CADENCE;
  }
  return cached;
}

/** Persist a new preference and notify every mounted surface. A no-op for an
 *  unchanged value, so callers can pass the current cadence without spamming
 *  re-renders or analytics. */
export function writeCadencePreference(next: BillingCadence): void {
  if (!isCadence(next)) return;
  if (readCadencePreference() === next) return;
  cached = next;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage blocked — the in-memory value still syncs every surface in this
      // tab, it just won't survive a reload. Better than throwing mid-click.
    }
  }
  emit();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  // Another tab (or another root in this tab) changed it — `storage` only fires
  // in OTHER tabs, so same-tab syncing is the listener set above.
  const onStorage = (e: StorageEvent) => {
    if (e.key !== null && e.key !== STORAGE_KEY) return;
    // key === null means the whole store was cleared.
    cached = isCadence(e.newValue) ? e.newValue : DEFAULT_CADENCE;
    emit();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(onChange);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

function getServerSnapshot(): BillingCadence {
  return DEFAULT_CADENCE;
}

/**
 * Read + set the shared cadence. Hydration-safe: the first client render uses
 * the same default the server rendered, and useSyncExternalStore re-reads the
 * stored value immediately after hydration, so a returning monthly user sees
 * one frame of annual rather than a hydration mismatch.
 */
export function useBillingCadence(): [
  BillingCadence,
  (next: BillingCadence) => void,
] {
  const cadence = useSyncExternalStore(
    subscribe,
    readCadencePreference,
    getServerSnapshot,
  );
  return [cadence, writeCadencePreference];
}

/** Test-only reset — clears the module cache AND storage so each case starts
 *  from the product default. */
export function __resetCadencePreferenceForTests(): void {
  cached = null;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to clear */
    }
  }
  emit();
}
